import React, { useState, useEffect, useRef } from 'react';
import { ocrImage } from './api';
import CameraComponent from './CameraComponent';

const getNewIngredient = () => ({ id: `temp-${Date.now()}-${Math.random()}`, name: '', amount: '', unit: '' });

const AddRecipeModal = ({ isOpen, onClose, onSave, editingRecipe, cookbooks, categories, masterIngredients, onAddCookbook, onAddCategory, onAddIngredient }) => {
  const [name, setName] = useState('');
  const [ingredients, setIngredients] = useState([getNewIngredient()]);
  const [cookbookId, setCookbookId] = useState('');
  const [pageNumber, setPageNumber] = useState('');
  const [servingSize, setServingSize] = useState('');
  const [categoryIds, setCategoryIds] = useState([]);
  const [cookbookSearchTerm, setCookbookSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [newCookbookCreated, setNewCookbookCreated] = useState(false);
  const [isCookbookDropdownOpen, setIsCookbookDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(-1);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const nameInputRef = useRef(null);
  const cookbookSearchTermInputRef = useRef(null);
  const pageNumberInputRef = useRef(null);
  const servingSizeInputRef = useRef(null);
  const modalRef = useRef(null);
  const ingredientsRef = useRef(null);
  const categoryInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (editingRecipe) {
        setName(editingRecipe.name);
        setIngredients([...editingRecipe.ingredients, getNewIngredient()]);
        setCookbookId(editingRecipe.cookbookId);
        setPageNumber(editingRecipe.pageNumber);
        setServingSize(editingRecipe.servingSize || '');
        setCategoryIds(editingRecipe.categoryIds || []);
      } else {
        setName('');
        setIngredients([getNewIngredient()]);
        setCookbookId('');
        setPageNumber('');
        setServingSize('');
        setCategoryIds([]);
        setCookbookSearchTerm('');
      }
      setError('');
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 0);
    } else {
      setNewCookbookCreated(false);
    }
  }, [isOpen, editingRecipe]);

  useEffect(() => {
    if (isOpen && editingRecipe && !newCookbookCreated) {
      const selectedCookbook = cookbooks.find(c => c.id === editingRecipe.cookbookId);
      setCookbookSearchTerm(selectedCookbook ? selectedCookbook.title : '');
    }
  }, [isOpen, editingRecipe, cookbooks, newCookbookCreated]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = Array.from(modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentIndex = focusableElements.indexOf(document.activeElement);

        event.preventDefault();

        if (event.shiftKey) {
          if (currentIndex === -1 || currentIndex === 0) {
            lastElement.focus();
          } else {
            focusableElements[currentIndex - 1].focus();
          }
        } else {
          if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
            firstElement.focus();
          } else {
            focusableElements[currentIndex + 1].focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, editingRecipe, ingredients, onClose]);

  if (!isOpen) return null;

  const handleIngredientChange = (index, field, value) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients[index][field] = value;
    if (field === 'name') {
      setOpenDropdownIndex(index);
    } else if (field === 'amount') {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^-?\d+(\.\d+)?\/-?\d+(\.\d+)?$/.test(trimmed)) {
          const parts = trimmed.split('/');
          const numerator = parseFloat(parts[0]);
          const denominator = parseFloat(parts[1]);
          if (!isNaN(numerator) && !isNaN(denominator)) {
            if (denominator != 0) {
              value = parseFloat((numerator / denominator).toFixed(3));
            }
          }
        } else if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
          const decimalValue = parseFloat(trimmed);
          if (!isNaN(decimalValue)) {
            value = parseFloat(decimalValue.toFixed(3));
          }
        }
      }
      updatedIngredients[index][field] = value;
    }
    setIngredients(updatedIngredients);
  };

  const handleIngredientSelect = (index, ingredientName) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients[index]['name'] = ingredientName;
    setIngredients(updatedIngredients);
    setOpenDropdownIndex(-1);
  };

  const handleAddNewIngredient = async (index, newIngredientName) => {
    const newIngredient = { name: newIngredientName };
    const result = await onAddIngredient(newIngredient, true);
    if (result && result.success) {
      handleIngredientSelect(index, result.ingredient.name);
    } else if (result && result.error) {
      setError(result.error);
    }
  };

  const addIngredientField = () => {
    setIngredients([...ingredients, getNewIngredient()]);
    setTimeout(() => {
      if (ingredientsRef.current) {
        const lastIngredient = ingredientsRef.current.lastElementChild;
        if (lastIngredient) {
          const input = lastIngredient.querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }
    }, 0);
  };

  const handleRemoveIngredient = (index) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients.splice(index, 1);
    setIngredients(updatedIngredients);
  };

  const handleCookbookSelect = (cookbook) => {
    setCookbookId(cookbook.id);
    setCookbookSearchTerm(cookbook.title);
    setIsCookbookDropdownOpen(false);
    if (pageNumberInputRef.current) {
      pageNumberInputRef.current.focus();
    }
  };

  const handleCategorySelect = (categoryId) => {
    if (!categoryIds.includes(categoryId)) {
      setCategoryIds([...categoryIds, categoryId]);
    }
    setCategorySearchTerm('');
    setIsCategoryDropdownOpen(false);
    if (categoryInputRef.current) {
      categoryInputRef.current.focus();
    }
  };

  const handleAddNewCookbook = async () => {
    const newCookbook = { title: cookbookSearchTerm };
    const result = await onAddCookbook(newCookbook, true);
    if (result && result.success) {
      setNewCookbookCreated(true);
      handleCookbookSelect(result.cookbook);
    } else if (result && result.error) {
      setError(result.error);
    }
  };

  const handleAddNewCategory = async () => {
    const newCategory = { title: categorySearchTerm };
    const result = await onAddCategory(newCategory, true);
    if (result && result.success) {
      handleCategorySelect(result.category.id);
    } else if (result && result.error) {
      setError(result.error);
    }
  };

  const handleIngredientKeyDown = async (e, index, field) => {
    if (e.key === 'Enter') {
      if (field === 'name') {
        const searchTerm = ingredients[index].name;
        const filtered = filteredMasterIngredients(searchTerm);
        const isNewIngredient = masterIngredients.every(i => i.name.toLowerCase() !== searchTerm.toLowerCase().trim());

        if (filtered.length === 1) {
          e.preventDefault();
          handleIngredientSelect(index, filtered[0].name);
          e.target.parentElement.parentElement.nextElementSibling.querySelector('input').focus();
        } else if (isNewIngredient && searchTerm.trim() !== '') {
          e.preventDefault();
          await handleAddNewIngredient(index, searchTerm);
          e.target.parentElement.parentElement.nextElementSibling.querySelector('input').focus();
        } else {
          if (ingredients[index].name.trim() !== '' && index === ingredients.length - 1) {
            e.preventDefault();
            addIngredientField();
          }
        }
      } else { // amount or unit
        const searchTerm = ingredients[index].name;
        const isNewIngredient = masterIngredients.every(i => i.name.toLowerCase() !== searchTerm.toLowerCase().trim());
        if (isNewIngredient && searchTerm.trim() !== '') {
          await handleAddNewIngredient(index, searchTerm);
        }
        if (ingredients[index].name.trim() !== '' && index === ingredients.length - 1) {
          e.preventDefault();
          addIngredientField();
        }
      }
    }
  };

  const handleSave = async (andAnother = false) => {
    if (!name) {
      setError('Recipe name cannot be empty');
      return;
    }
    const filteredIngredients = ingredients.filter(ing => ing.name.trim() !== '');
    const response = await onSave({ id: editingRecipe?.id, name, ingredients: filteredIngredients, cookbookId, pageNumber, categoryIds, servingSize: servingSize || 4 }, andAnother);
    if (response && response.error) {
      setError(response.error);
    } else {
      if (andAnother) {
        setName('');
        setIngredients([getNewIngredient()]);
        // setCookbookId(''); // Keep the cookbook selected
        setPageNumber('');
        setServingSize('');
        setCategoryIds([]);
        // setCookbookSearchTerm(''); // Keep the cookbook search term
        setCategorySearchTerm('');
        setError('');
        nameInputRef.current.focus();
      } else {
        onClose();
      }
    }
  };

  const handleCapture = async (blob) => {
    setShowCamera(false);
    setIsProcessing(true);
    try {
      const res = await ocrImage(blob, 'recipe');
      if (res && res.response) {
        try {
          const data = JSON.parse(res.response);
          if (data.title) setName(data.title);
          if (data.ingredients && Array.isArray(data.ingredients)) {
            const newIngredients = data.ingredients.map(ing => ({
              ...getNewIngredient(),
              name: ing.name || '',
              amount: ing.amount || '',
              unit: ing.unit || ''
            }));
            // Add an empty ingredient field at the end
            newIngredients.push(getNewIngredient());
            setIngredients(newIngredients);
          }
        } catch (parseError) {
          console.error('Error parsing JSON response:', parseError);
          setError('Failed to parse recipe data from image.');
        }
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setError(error.message || 'Error processing image.');
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredCookbooks = cookbooks
    .filter(cookbook =>
      cookbook.title.toLowerCase().includes(cookbookSearchTerm.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  const filteredCategories = categories
    .filter(category =>
      category.title.toLowerCase().includes(categorySearchTerm.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  const filteredMasterIngredients = (searchTerm) => masterIngredients
    .filter(ing =>
      ing.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));


  return (
    <div className="modal-overlay recipe-modal">
      <div className="modal-content" ref={modalRef}>
        <div className="modal-header">
          <h2><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>restaurant_menu</i>{editingRecipe ? 'Edit Recipe' : 'Add Recipe'}</h2>
          <button onClick={onClose} className="icon-button close-button" title="Close"><i className="material-icons">close</i></button>
        </div>
        {showCamera ? (
          <CameraComponent onCapture={handleCapture} onClose={() => setShowCamera(false)} />
        ) : (
          <>
            <div className="modal-body">
              {error && <p className="error-message">{error}</p>}
              <div className="search-input-container recipe-name-container">
                <input
                  ref={nameInputRef} data-tour="recipe-modal-name"
                  type="text"
                  placeholder="Recipe Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSave(!editingRecipe);
                    }
                  }}
                  disabled={isProcessing}
                />
                <button onClick={() => setShowCamera(true)} className="icon-button" title="Scan Recipe" disabled={isProcessing}><i className="material-icons">camera_alt</i></button>
                {isProcessing && (
                  <div className="clear-input-button" style={{ right: '50px' }}>
                    <i className="material-icons spin">autorenew</i>
                  </div>
                )}
                {name && !isProcessing && <button title="Clear name" onClick={() => { setName(''); nameInputRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
              </div>
              <div className="form-row" style={{ display: 'flex', gap: '1rem' }} data-tour="recipe-modal-cookbook">
                <div className="searchable-dropdown" style={{ flex: '4' }} onBlur={() => setTimeout(() => setIsCookbookDropdownOpen(false), 150)}>
                  <div className="search-input-container">
                    <input
                      ref={cookbookSearchTermInputRef}
                      type="text"
                      placeholder="Select cookbook..."
                      value={cookbookSearchTerm}
                      onChange={e => { setCookbookSearchTerm(e.target.value); setIsCookbookDropdownOpen(true); }}
                      onFocus={() => setIsCookbookDropdownOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if (filteredCookbooks.length === 1) {
                            e.preventDefault();
                            handleCookbookSelect(filteredCookbooks[0]);
                            document.querySelector('input[placeholder="Page"]').focus();
                          } else if (filteredCookbooks.length === 0 && cookbookSearchTerm) {
                            e.preventDefault();
                            handleAddNewCookbook();
                            document.querySelector('input[placeholder="Page"]').focus();
                          }
                        }
                      }}
                    />
                    {cookbookSearchTerm && <button title="Clear cookbook search" onClick={() => { setCookbookSearchTerm(''); setIsCookbookDropdownOpen(true); cookbookSearchTermInputRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
                  </div>
                  {isCookbookDropdownOpen && (
                    <ul className="dropdown-list">
                      {filteredCookbooks.map(cookbook => (
                        <li key={cookbook.id} onMouseDown={() => handleCookbookSelect(cookbook)}>
                          {cookbook.title}
                        </li>
                      ))}
                      {cookbookSearchTerm && !cookbooks.some(c => c.title.toLowerCase() === cookbookSearchTerm.trim().toLowerCase()) && (
                        <li onMouseDown={handleAddNewCookbook}>
                          Create new cookbook: "{cookbookSearchTerm}"
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                <div className="search-input-container" style={{ flex: '1' }}>
                  <input
                    ref={pageNumberInputRef}
                    type="number"
                    placeholder="Page"
                    value={pageNumber}
                    onChange={(e) => setPageNumber(e.target.value)}
                  />
                  {pageNumber && <button title="Clear page number" onClick={() => { setPageNumber(''); pageNumberInputRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
                </div>
              </div>
              <div className="form-row" style={{ display: 'flex', gap: '1rem' }} >
                <div className="search-input-container" style={{ flex: '1' }}>
                  <input
                    ref={servingSizeInputRef}
                    type="number"
                    placeholder="Serving size (default 4)"
                    value={servingSize}
                    onChange={(e) => setServingSize(e.target.value)}
                  />
                  {servingSize && <button title="Clear serving size" onClick={() => { setServingSize(''); servingSizeInputRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
                </div>
              </div>
              <div className="searchable-dropdown" onBlur={() => setTimeout(() => setIsCategoryDropdownOpen(false), 150)} data-tour="recipe-modal-categories">
                <div className="search-input-container">
                  <input
                    ref={categoryInputRef}
                    type="text"
                    placeholder="Select categories..."
                    value={categorySearchTerm}
                    onChange={e => { setCategorySearchTerm(e.target.value); setIsCategoryDropdownOpen(true); }}
                    onFocus={() => setIsCategoryDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (filteredCategories.length === 1) {
                          e.preventDefault();
                          handleCategorySelect(filteredCategories[0].id);
                          e.target.blur();
                        } else if (filteredCategories.length === 0 && categorySearchTerm) {
                          e.preventDefault();
                          handleAddNewCategory();
                          e.target.blur();
                        }
                      }
                    }}
                  />
                  {categorySearchTerm && <button title="Clear category search" onClick={() => { setCategorySearchTerm(''); setIsCategoryDropdownOpen(true); categoryInputRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
                </div>
                {isCategoryDropdownOpen && (
                  <ul className="dropdown-list">
                    {filteredCategories.map(category => (
                      <li key={category.id} onMouseDown={() => handleCategorySelect(category.id)}>
                        {category.title}
                      </li>
                    ))}
                    {categorySearchTerm && !categories.some(c => c.title.toLowerCase() === categorySearchTerm.trim().toLowerCase()) && (
                      <li onMouseDown={handleAddNewCategory}>
                        Create new category: "{categorySearchTerm}"
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <div className="selected-items">
                {categoryIds.map(id => {
                  const category = categories.find(c => c.id === id);
                  if (!category) return null;
                  return (
                    <div key={id} className="selected-item">
                      {category.title}
                      <button title="Remove category" onClick={() => setCategoryIds(categoryIds.filter(cid => cid !== id))} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
              <div className="ingredients-header" data-tour="recipe-modal-ingredients">
                <h3>Ingredients</h3>
                <button title="Add ingredient field" onClick={addIngredientField} className="plus-button"><i className="material-icons">add</i></button>
              </div>
              <div ref={ingredientsRef}>
                {ingredients.map((ingredient, index) => (
                  <div key={ingredient.id} className="ingredient-input" style={{ display: 'flex', gap: '1rem' }}>
                    <div className="searchable-dropdown" style={{ flex: '3' }} onBlur={() => setTimeout(() => setOpenDropdownIndex(-1), 150)}>
                      <div className="search-input-container">
                        <input
                          type="text"
                          placeholder="Name"
                          value={ingredient.name}
                          onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                          onFocus={() => setOpenDropdownIndex(index)}
                          onKeyDown={async (e) => await handleIngredientKeyDown(e, index, 'name')}
                        />
                        {ingredient.name && <button title="Clear ingredient name" onClick={(e) => {
                          handleIngredientChange(index, 'name', '');
                          const input = e.currentTarget.previousElementSibling;
                          if (input) {
                            input.focus();
                          }
                        }} className="clear-input-button"><i className="material-icons">close</i></button>}
                      </div>
                      {openDropdownIndex === index && (
                        <ul className="dropdown-list">
                          {filteredMasterIngredients(ingredient.name).map(ing => (
                            <li key={ing.id} onMouseDown={() => handleIngredientSelect(index, ing.name)}>
                              {ing.name}
                            </li>
                          ))}
                          {masterIngredients.every(i => i.name.toLowerCase() !== ingredient.name.toLowerCase().trim()) && ingredient.name.trim() && (
                            <li onMouseDown={() => handleAddNewIngredient(index, ingredient.name)}>
                              Create new ingredient: "{ingredient.name}"
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                    <div className="search-input-container" style={{ flex: '1' }}>
                      <input
                        type="text"
                        placeholder="Amount"
                        value={ingredient.amount}
                        onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                        onKeyDown={async (e) => await handleIngredientKeyDown(e, index, 'amount')}
                      />
                    </div>
                    <select
                      style={{ flex: '1' }}
                      value={ingredient.unit}
                      onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                      onKeyDown={async (e) => await handleIngredientKeyDown(e, index, 'unit')}
                    >
                      <option value=""></option>
                      <option value="tsp">tsp</option>
                      <option value="tbsp">tbsp</option>
                      <option value="cup">cup</option>
                      <option value="oz">oz</option>
                      <option value="lb">lb</option>
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                    </select>
                    <button title="Remove ingredient" onClick={() => handleRemoveIngredient(index)} className="icon-button delete-button"><i className="material-icons">delete</i></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              {!editingRecipe && <button title="Save and add another" onClick={() => handleSave(true)}>Save and another</button>}
              <button title="Save and close" onClick={() => handleSave(false)} data-tour="recipe-modal-save-close">Save and close</button>
              <button title="Cancel" onClick={onClose} className="cancel-button">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddRecipeModal;